/**
 * GuideReader.js — the V3 guides INSIDE the app.
 *
 * Until now HelpModal framed the backend's /tutorials-v3 site in an iframe: a
 * white page with someone else's navigation, header, footer and language switch
 * floating inside our dark workspace. Reading a guide meant reading a SECOND
 * APP, and the language of that app was the backend's cookie, not the language
 * the user picked in the File menu.
 *
 * This fetches the same pages and keeps only the GUIDE: the title, the summary,
 * the body and the prev/next pair. It renders them in the app's own theme, in
 * the app's own language (?lang= follows the app's EN/PT/ES, and the backend
 * serves translated guides — falling back to English where a guide has no
 * translation), and every link to another guide is followed IN APP. Nothing
 * here leaves the workspace.
 *
 * The parsing is deliberately forgiving: it reads the backend's own structure
 * (.tutorials-sidebar, .tutorial-header, .tutorial-body, .tutorial-nav) and
 * falls back to the iframe if any of it is missing, so a backend redesign
 * degrades to what we had before instead of showing a blank panel.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { HELP_URL } from './api';

export const GUIDES_BASE = '/tutorials-v3';

/** Internal guide link? '/tutorials-v3/zones' yes, the index itself, another
 *  site, or an anchor — no (those open in a tab or are left alone). */
export function isInternalGuideLink(href) {
  if (typeof href !== 'string') return false;
  const clean = href.split('#')[0].split('?')[0];
  return clean.startsWith(`${GUIDES_BASE}/`) && clean.length > GUIDES_BASE.length + 1;
}

/** The slug a guide link points at. */
export function guideSlugOf(href) {
  const clean = String(href || '').split('#')[0].split('?')[0];
  return isInternalGuideLink(clean) ? clean.slice(GUIDES_BASE.length + 1) : null;
}

/**
 * Strip anything that could execute or reach outside the article before the
 * HTML is shown. The source is our own backend, but "our own backend" is not a
 * sanitisation policy — and a guide body has no business carrying a script, a
 * frame, or an inline event handler.
 */
export function sanitizeNode(root) {
  const KILL = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form', 'button', 'input'];
  for (const el of Array.from(root.querySelectorAll(KILL.join(',')))) el.remove();
  for (const el of Array.from(root.querySelectorAll('*'))) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || '').trim().toLowerCase();
      const dangerousUrl = name === 'href' || name === 'src' || name === 'xlink:href';
      if (name.startsWith('on')
          || (dangerousUrl && (value.startsWith('javascript:') || value.startsWith('data:')))) {
        el.removeAttribute(attr.name);
      }
    }
  }
  return root;
}

/**
 * One .kv-video-card (the site's own markup) → plain data. The card's player is
 * a POSTER plus a click that swaps in the embed, and the sanitizer below kills
 * every iframe in an article — so the video is read out as data here and
 * rebuilt by the app as a React element, rather than copied across as HTML.
 */
function readVideoCard(card) {
  if (!card) return null;
  const btn = card.querySelector('[data-youtube-id]');
  const id = (btn && btn.getAttribute('data-youtube-id')) || '';
  if (!/^[A-Za-z0-9_-]{6,}$/.test(id)) return null;
  const caption = card.querySelector('.kv-video-caption');
  return {
    id,
    poster: (btn && btn.querySelector('img') && btn.querySelector('img').getAttribute('src')) || '',
    duration: (card.querySelector('.kv-video-duration')?.textContent || '').trim(),
    title: (caption && caption.querySelector('h3') ? caption.querySelector('h3').textContent : '').trim(),
    watchUrl: (caption && caption.querySelector('a[href]') ? caption.querySelector('a[href]').getAttribute('href') : '').trim(),
  };
}

/** Guide page → the walkthrough it belongs to, or null. */
export function parseGuideVideo(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  return readVideoCard(doc.querySelector('.kv-guide-video .kv-video-card'));
}

/** Index page → the whole walkthrough library, in the site's order. */
export function parseVideoLibrary(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  return Array.from(doc.querySelectorAll('.kv-video-card')).map(readVideoCard).filter(Boolean);
}

/**
 * Catalogue page → [{ category, slug, icon, title, summary }], in the site's
 * own order. Two shapes are read because the site has two: the INDEX page lists
 * cards under category headings, while a GUIDE page carries the whole catalogue
 * in its sidebar. Either one is enough to build the strip of chips, so the app
 * never hardcodes a list that goes stale when a guide is added or renamed.
 */
export function parseGuideIndex(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const out = [];
  const seen = new Set();
  const push = (item) => {
    if (item && item.slug && item.title && !seen.has(item.slug)) { seen.add(item.slug); out.push(item); }
  };

  for (const cat of Array.from(doc.querySelectorAll('.tutorials-category'))) {
    const category = (cat.querySelector('h2, h3, h4')?.textContent || '').trim();
    for (const a of Array.from(cat.querySelectorAll('a[href]'))) {
      const slug = guideSlugOf(a.getAttribute('href'));
      if (!slug) continue;
      push({
        category,
        slug,
        icon: (a.querySelector('.tutorial-card-icon')?.textContent || '').trim(),
        title: (a.querySelector('.tutorial-card-title')?.textContent || a.textContent || '').trim(),
        summary: (a.querySelector('.tutorial-card-summary')?.textContent || '').trim(),
      });
    }
  }
  if (out.length) return out;

  const scope = doc.querySelector('.tutorials-sidebar') || doc;
  let category = '';
  for (const node of Array.from(scope.querySelectorAll('h4, a'))) {
    if (node.tagName === 'H4') { category = node.textContent.trim(); continue; }
    const slug = guideSlugOf(node.getAttribute('href'));
    if (!slug) continue;
    const text = (node.textContent || '').trim().replace(/\s+/g, ' ');
    const icon = /^[^\s]+\s/.test(text) ? text.split(' ')[0] : '';
    const title = icon ? text.slice(icon.length).trim() : text;
    push({ category, slug, icon, title, summary: '' });
  }
  return out;
}

/** Guide page → { title, summary, icon, html, prev, next } or null. */
export function parseGuide(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const body = doc.querySelector('.tutorial-body');
  if (!body) return null;
  sanitizeNode(body);
  const header = doc.querySelector('.tutorial-header');
  const title = (header?.querySelector('h1')?.textContent || doc.querySelector('title')?.textContent || '').trim();
  const summary = (header?.querySelector('.tutorial-summary')?.textContent || '').trim();
  const icon = (header?.querySelector('.tutorial-icon')?.textContent || '').trim();
  const read = (sel) => {
    const a = doc.querySelector(sel);
    const slug = a ? guideSlugOf(a.getAttribute('href')) : null;
    if (!slug) return null;
    // The anchor carries its own "← Prev" label span; the button wants only the
    // guide's icon + name, so drop that span before reading the text.
    const clone = a.cloneNode(true);
    for (const span of Array.from(clone.querySelectorAll('.nav-label'))) span.remove();
    return { slug, label: (clone.textContent || '').trim().replace(/\s+/g, ' ') };
  };
  return {
    title, summary, icon, html: body.innerHTML,
    prev: read('.nav-prev'), next: read('.nav-next'),
    // The walkthrough that belongs to THIS guide (the site shows it above the
    // article; an iframe would be stripped, so it comes across as data).
    video: readVideoCard(doc.querySelector('.kv-guide-video .kv-video-card')),
  };
}

const GUIDE_CSS = `
.kv-guide { color: #cbd5e1; font-size: 0.86rem; line-height: 1.55; }
.kv-guide h2 { color: #e2e8f0; font-size: 1.02rem; margin: 1.1em 0 0.4em; }
.kv-guide h3 { color: #e2e8f0; font-size: 0.92rem; margin: 1em 0 0.35em; }
.kv-guide p { margin: 0 0 0.7em; }
.kv-guide strong { color: #e2e8f0; }
.kv-guide a { color: #93c5fd; text-decoration: none; cursor: pointer; }
.kv-guide a:hover { text-decoration: underline; }
.kv-guide ul, .kv-guide ol { margin: 0 0 0.8em; padding-left: 1.25em; }
.kv-guide li { margin: 0 0 0.35em; }
.kv-guide kbd { background: #1e293b; border: 1px solid #475569; border-radius: 4px;
  padding: 1px 5px; font: 600 0.74rem ui-monospace, monospace; color: #e2e8f0; }
.kv-guide code { background: #1e293b; border-radius: 4px; padding: 1px 4px; color: #e2e8f0; }
.kv-guide .tip { background: #111c33; border-left: 3px solid #6366f1; border-radius: 6px;
  padding: 0.55rem 0.7rem; margin: 0.8em 0; color: #c7d2fe; }
.kv-guide table { width: 100%; border-collapse: collapse; margin: 0.8em 0; }
.kv-guide th, .kv-guide td { text-align: left; padding: 0.4rem 0.5rem; border-bottom: 1px solid #334155; color: #94a3b8; }
.kv-guide th { color: #e2e8f0; }
.kv-guide-video-card { margin: 0 0 12px; }
.kv-guide-video-card button { background: none; border: 0; padding: 0; cursor: pointer; display: block; width: 100%; }
.kv-guide-video-card img { width: 100%; display: block; border-radius: 8px; border: 1px solid #334155; }
`;

/**
 * A walkthrough, rebuilt in the app's own chrome: poster first, the embed only
 * once a finger taps it (the site's own pattern — no YouTube load for a reader
 * who never wanted the video), and a plain link out for anyone who wants the
 * real thing.
 */
function VideoCard({ video, t, compact = false }) {
  const [playing, setPlaying] = useState(false);
  if (!video) return null;
  return (
    <div className="kv-guide-video-card" style={compact ? { maxWidth: 340 } : undefined}>
      {playing ? (
        <iframe
          title={video.title || t('help.videos')}
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.id)}?autoplay=1&rel=0`}
          allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          style={{ width: '100%', aspectRatio: '16 / 9', border: '1px solid #334155', borderRadius: 8, background: '#000' }}
        />
      ) : (
        <button
          type="button"
          aria-label={t('help.videoPlay', { title: video.title || t('help.videos') })}
          title={t('help.videoPlay', { title: video.title || t('help.videos') })}
          onClick={() => setPlaying(true)}
        >
          <span style={{ position: 'relative', display: 'block' }}>
            {video.poster ? (
              <img src={video.poster} alt="" loading="lazy" decoding="async" />
            ) : (
              <span style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} />
            )}
            <span style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '1.6rem', textShadow: '0 2px 10px rgba(0,0,0,0.8)',
            }} aria-hidden="true">▶</span>
            {video.duration ? (
              <span style={{
                position: 'absolute', right: 8, bottom: 8, background: 'rgba(2,6,23,0.85)', color: '#e2e8f0',
                fontSize: '0.68rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              }} aria-hidden="true">{video.duration}</span>
            ) : null}
          </span>
        </button>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4, fontSize: '0.74rem' }}>
        <span style={{ color: '#cbd5e1' }}>{video.title}</span>
        {video.watchUrl ? (
          <a
            href={video.watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#93c5fd', textDecoration: 'none', flexShrink: 0 }}
          >{t('help.videoWatch')}</a>
        ) : null}
      </div>
    </div>
  );
}

export default function GuideReader() {
  const { t, lang } = useI18n();
  const [index, setIndex] = useState(null);   // null = not loaded yet, [] = failed
  const [library, setLibrary] = useState([]); // walkthroughs, read from the same page
  const [showVideos, setShowVideos] = useState(false);
  const [slug, setSlug] = useState(null);
  const [guide, setGuide] = useState(null);
  const [failed, setFailed] = useState(false);
  const bodyRef = useRef(null);

  // The catalogue comes from the backend, so the app never carries a list that
  // goes stale when a guide is added or renamed.
  useEffect(() => {
    let alive = true;
    fetch(`${HELP_URL}?lang=${encodeURIComponent(lang)}`, { credentials: 'omit' })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error('index'))))
      .then((html) => {
        if (!alive) return;
        const list = parseGuideIndex(html);
        setIndex(list);
        // The walkthrough library lives on the same page — no second fetch, and
        // the app never hardcodes which videos exist.
        setLibrary(parseVideoLibrary(html));
        setSlug(prev => prev || (list[0] && list[0].slug) || null);
      })
      .catch(() => { if (alive) { setIndex([]); setFailed(true); } });
    return () => { alive = false; };
  }, [lang]);

  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;
    setFailed(false);
    fetch(`${HELP_URL}/${slug}?lang=${encodeURIComponent(lang)}`, { credentials: 'omit' })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error('guide'))))
      .then((html) => {
        if (!alive) return;
        const parsed = parseGuide(html);
        if (!parsed) { setFailed(true); return; }
        setGuide(parsed);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [slug, lang]);

  // Links inside the article: another guide opens HERE; anything else opens in
  // a tab, because the app has no version of it.
  const onBodyClick = useCallback((e) => {
    const a = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const next = guideSlugOf(href);
    if (next) { e.preventDefault(); setSlug(next); return; }
    if (/^https?:/i.test(href)) { e.preventDefault(); window.open(href, '_blank', 'noopener'); }
  }, []);

  const chip = (active) => ({
    flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 9px', borderRadius: 999, cursor: 'pointer', fontSize: '0.74rem',
    border: `1px solid ${active ? '#6366f1' : '#334155'}`,
    background: active ? 'rgba(99,102,241,0.28)' : '#0f172a',
    color: active ? '#e0e7ff' : '#94a3b8',
  });

  const navBtn = {
    padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem',
    border: '1px solid #334155', background: '#0f172a', color: '#cbd5e1',
  };

  const grouped = useMemo(() => {
    const groups = [];
    for (const item of (index || [])) {
      const last = groups[groups.length - 1];
      if (last && last.category === item.category) last.items.push(item);
      else groups.push({ category: item.category, items: [item] });
    }
    return groups;
  }, [index]);

  if (failed || (index && index.length === 0)) {
    // Degraded, not broken: the framed site still works, and the note says why.
    return (
      <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: '0.74rem', color: '#fbbf24' }}>{t('help.guideFallback')}</div>
        <iframe
          src={`${HELP_URL}?lang=${encodeURIComponent(lang)}`}
          title={t('help.guides')}
          style={{ flex: '1 1 auto', minHeight: 0, width: '100%', border: '1px solid #334155', borderRadius: 8, background: '#fff' }}
        />
      </div>
    );
  }

  return (
    <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <style>{GUIDE_CSS}</style>
      {/* The catalogue — a strip of chips, not a sidebar: it reads the same on
          a phone as on a desktop and never costs a second navigation. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 6px', alignItems: 'center' }}>
        {grouped.map(group => (
          <React.Fragment key={group.category || 'x'}>
            {group.category ? (
              <span style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569', marginLeft: 4 }}>
                {group.category}
              </span>
            ) : null}
            {group.items.map(item => (
              <button
                key={item.slug}
                type="button"
                title={item.summary || item.title}
                onClick={() => { setShowVideos(false); setSlug(item.slug); }}
                style={chip(!showVideos && slug === item.slug)}
              >
                {item.icon ? <span aria-hidden="true">{item.icon}</span> : null}{item.title}
              </button>
            ))}
          </React.Fragment>
        ))}
        {/* The walkthroughs belong here too: the site hides them behind a
            "full site" hop, but a reader who wants to WATCH should not leave. */}
        {library.length ? (
          <button
            type="button"
            onClick={() => setShowVideos(true)}
            style={chip(showVideos)}
            title={t('help.videosHint')}
          >▶ {t('help.videos')}</button>
        ) : null}
        {!index ? <span style={{ fontSize: '0.74rem', color: '#64748b' }}>{t('help.guideLoading')}</span> : null}
      </div>

      <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '0 2px' }}>
        {showVideos ? (
          <div className="kv-guide">
            <h2 style={{ margin: '0 0 4px', fontSize: '1.05rem', color: '#f1f5f9' }}>{t('help.videos')}</h2>
            <div style={{ fontSize: '0.76rem', color: '#94a3b8', margin: '0 0 10px' }}>{t('help.videoNote')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {library.map(v => <VideoCard key={v.id} video={v} t={t} />)}
            </div>
          </div>
        ) : guide ? (
          <div className="kv-guide">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
              {guide.icon ? <span style={{ fontSize: '1.1rem' }} aria-hidden="true">{guide.icon}</span> : null}
              <h2 style={{ margin: 0, fontSize: '1.05rem', color: '#f1f5f9' }}>{guide.title}</h2>
            </div>
            {guide.summary ? (
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0 0 10px' }}>{guide.summary}</div>
            ) : null}
            {/* The walkthrough that belongs to this guide, in the same place the
                site puts it — reading about cones and watching cones are one
                gesture apart here, not a hop to another website. Kept modest so
                the article stays the point of the page. */}
            <VideoCard video={guide.video} t={t} compact />
            <div ref={bodyRef} onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: guide.html }} />
          </div>
        ) : (
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{t('help.guideLoading')}</div>
        )}
      </div>

      {!showVideos && guide && (guide.prev || guide.next) ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {guide.prev
            ? <button type="button" style={navBtn} onClick={() => setSlug(guide.prev.slug)}>← {guide.prev.label}</button>
            : <span />}
          {guide.next
            ? <button type="button" style={navBtn} onClick={() => setSlug(guide.next.slug)}>{guide.next.label} →</button>
            : <span />}
        </div>
      ) : null}
    </div>
  );
}
